export interface DeviceView {
  id: string;
  name: string;
  customer: string | null;
  os: string;
  sshUser: string;
  online: boolean;
  status: string;
  agentVersion: string | null;
  breakglassPort: number;
  lastSeenAt: string | null;
  /** Online AND the agent advertised the 'desktop' capability. */
  desktopCapable: boolean;
  /** A live desktop session already holds this box (single-viewer exclusivity). */
  desktopBusy: boolean;
  /** Online AND the agent advertised 'file-drop' — drag-and-drop enabled. */
  fileDropCapable: boolean;
  /** Online AND the agent advertised 'file-browse' — the Files button works. */
  fileBrowseCapable: boolean;
  /** What the broker serves for this platform (null: no artifact uploaded). */
  latestVersion: string | null;
  /** The broker's artifact is newer than what the box last reported. */
  updateAvailable: boolean;
  /** Online AND the agent can take `update_now` — enables the Update action. */
  canPushUpdate: boolean;
}

export interface SessionView {
  id: string;
  deviceId: string;
  deviceName: string | null;
  username: string | null;
  kind: 'ssh' | 'tunnel' | 'file';
  startedAt: string;
  endedAt: string | null;
  bytesUp: string;
  bytesDown: string;
  /** For 'file' rows: the operation and path ("fetch C:\\MES\\report.pdf"). */
  detail: string | null;
}

const TOKEN_KEY = 'dikonek.tunnel.token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Machine token from the body ('device_offline', …); absent on older routes. */
    readonly code?: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    // An expired 30d token is the common 401 here — drop it so the app falls
    // back to the login screen instead of looping on failed polls.
    if (res.status === 401) clearToken();
    const body = (await res.json().catch(() => ({}))) as {
      message?: string | string[];
      errorCode?: string;
      code?: string;
    };
    const msg = Array.isArray(body.message) ? body.message.join(', ') : body.message;
    // `errorCode` is what actually reaches the wire: the shared
    // AllExceptionsFilter rebuilds every error body from a fixed key set and
    // drops a top-level `code`. `code` is kept as a fallback so any endpoint
    // that bypasses the filter still works.
    throw new ApiError(
      msg ?? `${res.status} ${res.statusText}`,
      res.status,
      body.errorCode ?? body.code,
    );
  }
  return (await res.json()) as T;
}

export interface UploadHandle {
  promise: Promise<{ path: string; bytes: number }>;
  abort: () => void;
}

/**
 * Drag-and-drop delivery to a box. XHR, not fetch — fetch still has no upload
 * progress events, and a progress bar is the whole point of dropping a big
 * installer instead of mailing a flashdisk.
 */
export function uploadDeviceFile(
  deviceId: string,
  file: File,
  onProgress: (sent: number, total: number) => void,
  /** File-browser "Upload here": absolute folder on the box. Omit for the drop folder. */
  dir?: string,
): UploadHandle {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<{ path: string; bytes: number }>((resolve, reject) => {
    const query = new URLSearchParams({ name: file.name });
    if (dir) query.set('dir', dir);
    xhr.open('POST', `/api/devices/${deviceId}/files?${query.toString()}`);
    const token = getToken();
    if (token) xhr.setRequestHeader('authorization', `Bearer ${token}`);
    // Never the file's own type: a dropped .json with a JSON content-type
    // would be eaten by the broker's body parser instead of streamed.
    xhr.setRequestHeader('content-type', 'application/octet-stream');
    xhr.upload.onprogress = (event) => onProgress(event.loaded, event.total || file.size);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText) as { path: string; bytes: number });
        return;
      }
      let message = `${xhr.status} ${xhr.statusText}`;
      try {
        const body = JSON.parse(xhr.responseText) as { message?: string | string[] };
        const m = Array.isArray(body.message) ? body.message.join(', ') : body.message;
        if (m) message = m;
      } catch {
        // non-JSON error body — keep the status line
      }
      reject(new ApiError(message, xhr.status));
    };
    xhr.onerror = () => reject(new ApiError('the upload connection failed', 0));
    xhr.onabort = () => reject(new ApiError('upload canceled', 0));
    xhr.send(file);
  });
  return { promise, abort: () => xhr.abort() };
}

// ─── File browser ───

export interface FsEntry {
  name: string;
  size: number;
  mtimeMs: number;
  isDir: boolean;
}

/** path '' lists the roots (drive letters on Windows, / on Linux). */
export function listDeviceFs(
  deviceId: string,
  path: string,
): Promise<{ entries: FsEntry[]; truncated: boolean }> {
  return request(`/devices/${deviceId}/fs?path=${encodeURIComponent(path)}`);
}

export function deleteDeviceFile(deviceId: string, path: string): Promise<{ deleted: string }> {
  return request(`/devices/${deviceId}/fs?path=${encodeURIComponent(path)}`, { method: 'DELETE' });
}

/**
 * Hand the browser a URL it can download normally (progress bar, cancel,
 * straight to disk — none of which we would get by buffering the file in JS).
 * A link cannot carry the Bearer header, so we first trade the token for a
 * single-use 60s ticket and put THAT in the URL.
 */
export async function deviceDownloadUrl(deviceId: string, path: string): Promise<string> {
  const { token } = await request<{ token: string }>(`/devices/${deviceId}/fs/download-token`, {
    method: 'POST',
    body: JSON.stringify({ path }),
  });
  return `/api/devices/${deviceId}/fs/download?token=${encodeURIComponent(token)}`;
}

export async function login(
  username: string,
  password: string,
): Promise<{ mustChangePassword: boolean }> {
  const result = await request<{ accessToken: string; mustChangePassword: boolean }>(
    '/auth/login',
    { method: 'POST', body: JSON.stringify({ username, password }) },
  );
  setToken(result.accessToken);
  return { mustChangePassword: result.mustChangePassword };
}

/** Rotates the password, revokes all old tokens, and stores the fresh one. */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const result = await request<{ accessToken: string }>('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  setToken(result.accessToken);
}

export function listSessions(): Promise<SessionView[]> {
  return request<SessionView[]>('/sessions?limit=200');
}

export function listDevices(): Promise<DeviceView[]> {
  return request<DeviceView[]>('/devices');
}

export function createEnrollToken(body: {
  name?: string;
  customer?: string;
}): Promise<{ token: string; expiresAt: string }> {
  return request('/enroll-tokens', { method: 'POST', body: JSON.stringify(body) });
}

export function revokeDevice(id: string): Promise<{ id: string; name: string; status: string }> {
  return request(`/devices/${id}/revoke`, { method: 'POST' });
}

/** Push `update_now` at a connected agent; 409s carry a printable reason. */
export function pushDeviceUpdate(
  id: string,
): Promise<{ pushed: boolean; id: string; name: string }> {
  return request(`/devices/${id}/update`, { method: 'POST', body: JSON.stringify({}) });
}

export function deleteDevice(
  id: string,
): Promise<{ id: string; name: string; sessionsDeleted: number }> {
  return request(`/devices/${id}`, { method: 'DELETE' });
}

export function updateDevice(
  id: string,
  patch: { sshUser?: string; customer?: string },
): Promise<{ id: string; name: string; sshUser: string; customer: string | null }> {
  return request(`/devices/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

// ─── MES add-on pack downloads (MES 5.1 packs, pack service log) ───

export type PackDownloadResult = 'sent' | 'license_invalid' | 'not_licensed' | 'not_published';

/** One POST /api/mes-packs/download as the broker logged it (newest first). */
export interface PackDownloadView {
  id: string;
  at: string;
  /** Claimed by the licence; unverified when result is license_invalid. */
  licenseId: string | null;
  customer: string | null;
  addonId: string;
  platformVersion: string;
  platformBuildId: string;
  result: PackDownloadResult;
  remoteAddress: string | null;
}

export function listPackDownloads(limit = 100): Promise<PackDownloadView[]> {
  return request<PackDownloadView[]>(`/mes-packs/downloads?limit=${limit}`);
}

// ─── Published downloads (the public /downloads/ folder + agent binaries) ───

/** One installer in the public /downloads/ folder (GET /api/downloads). */
export interface DownloadFile {
  name: string;
  sizeBytes: number;
  /** ISO 8601 mtime — when it was published. */
  modifiedAt: string;
  /** Origin-relative: /downloads/<encoded name>. */
  url: string;
  /** null while the broker is still hashing it — ask again shortly. */
  sha256: string | null;
}

/** An agent binary the broker serves (the same answer as /api/agent/latest). */
export interface AgentBinary {
  version: string;
  sha256: string;
  sizeBytes: number;
  url: string;
  modifiedAt: string;
}

export interface DownloadsListing {
  /** Where nginx serves the folder: '/downloads/'. */
  publicBase: string;
  /** The folder on the VPS (publish target). */
  dir: string;
  /** Newest first. */
  files: DownloadFile[];
  /** A platform is absent when the broker serves no agent for it. */
  agent: { win32?: AgentBinary; linux?: AgentBinary };
}

export function listDownloads(): Promise<DownloadsListing> {
  return request<DownloadsListing>('/downloads');
}

// ─── Public HTTP tunnels ───

export interface TunnelView {
  id: string;
  subdomain: string;
  url: string;
  deviceId: string | null;
  deviceName: string | null;
  mode: 'persistent' | 'ephemeral';
  localHost: string;
  localPort: number;
  rewriteHost: boolean;
  authMode: 'none' | 'basic';
  authUsername: string | null;
  online: boolean;
  bytesIn: string;
  bytesOut: string;
  lastActiveAt: string | null;
  createdBy: string;
  createdAt: string;
}

export interface TunnelInput {
  subdomain: string;
  device: string;
  localHost?: string;
  localPort: number;
  rewriteHost?: boolean;
  authUsername?: string;
  authPassword?: string;
}

export function listTunnels(): Promise<TunnelView[]> {
  return request<TunnelView[]>('/tunnels');
}

export function createTunnel(input: TunnelInput): Promise<TunnelView> {
  return request('/tunnels', {
    method: 'POST',
    body: JSON.stringify({ ...input, mode: 'persistent' }),
  });
}

export function updateTunnel(
  id: string,
  patch: Partial<Omit<TunnelInput, 'subdomain' | 'device'>>,
): Promise<TunnelView> {
  return request(`/tunnels/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export function deleteTunnel(id: string): Promise<{ id: string; subdomain: string }> {
  return request(`/tunnels/${id}`, { method: 'DELETE' });
}

// ─── Remote desktop ───

/** What POST /desktop/sessions hands back — enough to open the viewer socket. */
export interface DesktopSessionHandle {
  sessionId: string;
  deviceId: string;
  deviceName: string;
  status: string;
  expiresAt: string;
}

/** One audit row: who took control of which box, for how long, and how much moved. */
export interface DesktopSessionView {
  id: string;
  deviceId: string;
  /** Denormalized at write time so the audit outlives the device row. */
  deviceName: string;
  username: string;
  status: string;
  startedAt: string;
  endedAt: string | null;
  endReason: string | null;
  bytesRelayed: string;
}

/** `device` is a name or uuid, same as the tunnels API. */
export function createDesktopSession(device: string): Promise<DesktopSessionHandle> {
  return request('/desktop/sessions', { method: 'POST', body: JSON.stringify({ device }) });
}

export function endDesktopSession(id: string): Promise<{ id: string; status: string }> {
  return request(`/desktop/sessions/${id}`, { method: 'DELETE' });
}

export function listDesktopSessions(): Promise<DesktopSessionView[]> {
  return request<DesktopSessionView[]>('/desktop/sessions?limit=50');
}
